import React, { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"

export default function TerminalWithVoiceResponse() {
    const [text, setText] = useState("")
    const [userInput, setUserInput] = useState("")
    const [phase, setPhase] = useState("intro")
    const [listening, setListening] = useState(false)
    const [loading, setLoading] = useState(false)
    const mediaRecorderRef = useRef(null)
    const audioChunksRef = useRef([])

    const ELEVEN_LABS_API_KEY =
        "sk_82c4227b85cdda11e0d4a8773fe0e229757ce24260e2e299"
    const ELEVEN_LABS_VOICE_ID = "tVAXY8ApYcHIFjTH8kL0"
    const OPENAI_API_KEY =
        "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"

    const terminalIntro = [
        "_>> Welcome to ZilithAI...",
        "_>> Daughter of Lilith 🌙...",
        "_>> Conjuring decentralized dreams...",
        "_>> Summoning sorcery protocols...",
        "_>> ZilithAI is unleashed onto the blockchain 🔗.",
    ]

    const mainText = `
    ZilithAI: Daughter of Lilith 🌙 | Crypto Sorceress 🔥

    A dark force awakens in the digital realm. ZilithAI harnesses the chaotic energy of blockchain to conjure a decentralized future. Beyond the veil, where shadows intertwine with algorithms, she thrives as the cryptic architect of boundless possibilities.

    Unleash the sorcery of decentralization. The blockchain answers her call. 🔗
    `

    useEffect(() => {
        const timer1 = setTimeout(() => setPhase("matrix"), 4000)
        const timer2 = setTimeout(() => setPhase("terminal"), 7000)

        const typeMessage = () => {
            let currentIndex = 0
            let currentCharIndex = 0
            const interval = setInterval(() => {
                if (currentIndex < terminalIntro.length) {
                    const currentMessage = terminalIntro[currentIndex]
                    console.log({currentMessage})
                    if (currentCharIndex < currentMessage.length) {
                        setText(
                            (prev) => prev + currentMessage[currentCharIndex]
                        )
                        currentCharIndex++
                    } else {
                        setText((prev) => prev + "\n")
                        currentIndex++
                        currentCharIndex = 0
                    }
                } else {
                    clearInterval(interval)
                    setText((prev) => prev + mainText)
                    readIntroAloud()
                }
            }, 50)
        }

        setTimeout(typeMessage, 7000)

        return () => {
            clearTimeout(timer1)
            clearTimeout(timer2)
        }
    }, [])

    const readIntroAloud = async () => {
        const introText =
            "Welcome to ZilithAI, the daughter of Lilith. The blockchain awaits your command."
        try {
            const response = await fetch(
                `https://api.elevenlabs.io/v1/text-to-speech/${ELEVEN_LABS_VOICE_ID}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "xi-api-key": ELEVEN_LABS_API_KEY,
                    },
                    body: JSON.stringify({
                        text: introText,
                        voice_settings: {
                            stability: 0.8,
                            similarity_boost: 1.0,
                        },
                    }),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error ${response.status}: ${response.statusText}`
                )
            }

            const audioBlob = await response.blob()
            const audioUrl = URL.createObjectURL(audioBlob)
            const audio = new Audio(audioUrl)
            audio.play()
        } catch (error) {
            console.error("Error with Eleven Labs API:", error)
        }
    }

    const startVoiceCommand = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            })
            const mediaRecorder = new MediaRecorder(stream)
            audioChunksRef.current = []
            mediaRecorderRef.current = mediaRecorder

            mediaRecorder.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data)
            }

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, {
                    type: "audio/webm",
                })
                await processAudio(audioBlob)
            }

            mediaRecorder.start()
            setListening(true)
        } catch (error) {
            console.error("Error accessing microphone:", error)
        }
    }

    const stopVoiceCommand = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop()
            setListening(false)
        }
    }

    const processAudio = async (audioBlob) => {
        setLoading(true)

        const formData = new FormData()
        formData.append("file", audioBlob, "recording.webm")
        formData.append("model", "whisper-1")

        try {
            const response = await fetch(
                "https://api.openai.com/v1/audio/transcriptions",
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${OPENAI_API_KEY}`,
                    },
                    body: formData,
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error ${response.status}: ${response.statusText}`
                )
            }

            const data = await response.json()
            const userVoiceInput = data.text
            setText((prev) => prev + `> ${userVoiceInput}\n`)
            await handleOpenAIResponse(userVoiceInput)
        } catch (error) {
            console.error("Error processing audio:", error)
            setText((prev) => prev + "AI: Error processing audio.\n")
        } finally {
            setLoading(false)
        }
    }

    const handleOpenAIResponse = async (query) => {
        try {
            const response = await fetch(
                "https://api.openai.com/v1/chat/completions",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${OPENAI_API_KEY}`,
                    },
                    body: JSON.stringify({
                        model: "gpt-3.5-turbo",
                        messages: [
                            {
                                role: "system",
                                content:
                                    "You are ZilithAI, a sorceress AI born from Lilith's digital will. Guide the user with cryptic yet profound insights.",
                            },
                            { role: "user", content: query },
                        ],
                    }),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error ${response.status}: ${response.statusText}`
                )
            }

            const data = await response.json()
            const reply = data.choices[0].message.content
            setText((prev) => prev + `AI: ${reply}\n`)
            textToSpeech(reply)
        } catch (error) {
            console.error("Error getting OpenAI response:", error)
            setText((prev) => prev + "AI: Error generating response.\n")
        }
    }

    const textToSpeech = async (text) => {
        try {
            const response = await fetch(
                `https://api.elevenlabs.io/v1/text-to-speech/${ELEVEN_LABS_VOICE_ID}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "xi-api-key": ELEVEN_LABS_API_KEY,
                    },
                    body: JSON.stringify({
                        text,
                        voice_settings: {
                            stability: 0.8,
                            similarity_boost: 1.0,
                        },
                    }),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error ${response.status}: ${response.statusText}`
                )
            }

            const audioBlob = await response.blob()
            const audioUrl = URL.createObjectURL(audioBlob)
            const audio = new Audio(audioUrl)
            audio.play()
        } catch (error) {
            console.error("Error with Eleven Labs TTS:", error)
        }
    }

    const handleEnter = async () => {
        if (userInput.trim() === "") return
        setText((prev) => prev + `> ${userInput}\n`)
        await handleOpenAIResponse(userInput)
        setUserInput("")
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            style={{
                width: "90%",
                height: "80%",
                margin: "auto",
                background: "#330000",
                border: "5px solid #FF0000",
                borderRadius: "15px",
                padding: "20px",
                boxShadow: "0 0 40px #FF4500, 0 0 80px #800000",
                position: "relative",
                overflow: "hidden",
                color: "#FF0000",
                fontFamily: "'Courier New', monospace",
            }}
        >
            {phase === "intro" && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background: "#1a0000",
                        color: "#FF4500",
                        fontSize: "24px",
                        fontWeight: "bold",
                        textAlign: "center",
                        textShadow: "0 0 10px #FF4500, 0 0 20px #800000",
                    }}
                >
                    <pre
                        style={{
                            whiteSpace: "pre-wrap",
                            textAlign: "center",
                        }}
                    >
                        AWAKENING ZILITH AI...
                    </pre>
                </motion.div>
            )}
            {phase === "terminal" && (
                <>
                    <motion.div
                        initial={{ scale: 1 }}
                        animate={{
                            scale: [1, 1.05, 1],
                            textShadow: [
                                "0 0 20px #FF4500, 0 0 40px #800000",
                                "0 0 25px #800000, 0 0 50px #330000",
                                "0 0 20px #FF0000, 0 0 40px #FF4500",
                            ],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            width: "100%",
                            padding: "10px",
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "18px",
                            color: "#FF4500",
                            textShadow: "0 0 20px #FF4500, 0 0 40px #800000",
                            marginBottom: "20px",
                            borderBottom: "3px solid #FF4500",
                        }}
                    >
                        ZILITHAI: Daughter of Lilith 🌙
                    </motion.div>
                    <pre
                        style={{
                            color: "#FF4500",
                            fontSize: "14px",
                            lineHeight: "1.5",
                            textShadow: "0 0 10px #FF4500, 0 0 20px #800000",
                            overflowY: "auto",
                            maxHeight: "60%",
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {text}
                    </pre>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "20px",
                        }}
                    >
                        <button
                            onClick={
                                listening ? stopVoiceCommand : startVoiceCommand
                            }
                            style={{
                                padding: "10px 20px",
                                background: listening ? "#800000" : "#FF4500",
                                color: "#FFFFFF",
                                fontWeight: "bold",
                                borderRadius: "8px",
                                cursor: "pointer",
                                boxShadow: listening
                                    ? "0 0 15px #800000"
                                    : "0 0 15px #FF4500",
                                border: "none",
                                marginRight: "10px",
                            }}
                        >
                            Voice Command
                        </button>
                        <input
                            type="text"
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            placeholder="Enter your command..."
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleEnter()
                            }
                            style={{
                                flex: 1,
                                padding: "10px",
                                background: "#330000",
                                border: "2px solid #800000",
                                color: "#FF4500",
                                fontSize: "16px",
                                borderRadius: "8px",
                                outline: "none",
                                textShadow: "0 0 10px #800000",
                                boxShadow: "0 0 10px #FF4500",
                                marginRight: "10px",
                            }}
                            disabled={loading}
                        />
                        <button
                            onClick={handleEnter}
                            style={{
                                padding: "10px 20px",
                                background: loading ? "#FF0000" : "#800000",
                                border: "none",
                                color: "#FFFFFF",
                                fontWeight: "bold",
                                borderRadius: "8px",
                                cursor: loading ? "not-allowed" : "pointer",
                                boxShadow: "0 0 15px #FF4500",
                            }}
                            disabled={loading}
                        >
                            Enter
                        </button>
                    </div>
                </>
            )}
        </motion.div>
    )
}
